import React from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from './ErrorBoundary';
import { PageErrorFallback } from './PageErrorFallback';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  homeHref?: string;
}

/**
 * Page-level boundary for use inside a layout, around <Outlet />. The surrounding
 * chrome (header, nav, footer) stays mounted, so only the page content is replaced.
 *
 * The `key={pathname}` is load-bearing: a class boundary latches its error state
 * forever once caught. Re-keying on the location remounts it on navigation, so a
 * user who hits a broken page can simply click elsewhere and carry on. Without it
 * they would stay stuck on the error card for the rest of the session.
 */
export const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({
  children,
  homeHref,
}) => {
  const { pathname } = useLocation();

  return (
    <ErrorBoundary
      key={pathname}
      source={`route:${pathname}`}
      fallback={(error, reset) => (
        <PageErrorFallback error={error} reset={reset} homeHref={homeHref} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
};

export default RouteErrorBoundary;
